// Package E — Renders the PDF model into a print-ready HTML document.
// Reuses the EXACT unit-aware formatting rules already established in
// TakeoffReportPage.jsx (never combining incompatible units, never
// producing a mixed-unit numeric total) — no new formatting logic
// invented here, only translated to plain HTML/CSS for print.
function formatQty(value, unit) {
  const isCountUnit = /^(ea|unit|no|nos)$/i.test((unit || '').trim())
  const n = Number(value)
  const formatted = isCountUnit ? Math.round(n).toLocaleString('en-US') : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return `${formatted} ${unit || ''}`.trim()
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
}

export function renderTakeoffReportPdf(model) {
  const summaryRowsHtml = model.summaryRows.map((row, idx) => {
    const showCategoryName = idx === 0 || model.summaryRows[idx - 1].categoryId !== row.categoryId
    return `<tr>
      <td class="cat-cell">${showCategoryName ? `<span class="cat-code">${String(row.categoryIndex).padStart(2, '0')}</span> ${esc(row.categoryName.toUpperCase())}` : ''}</td>
      <td class="unit-cell">${esc(row.unit)}</td>
      <td class="qty-cell">${formatQty(row.total, row.unit)}</td>
    </tr>`
  }).join('')

  const categoriesHtml = model.categories.map((cat, catIdx) => {
    const codeLabel = String(catIdx + 1).padStart(2, '0')
    const catTotalsHtml = cat.totalsByUnit.map(([unit, qty]) => `<div>${formatQty(qty, unit)}</div>`).join('')
    const itemsHtml = cat.items.map((item, itemIdx) => {
      const itemCode = `${codeLabel}.${String(itemIdx + 1).padStart(2, '0')}`
      const sheetsHtml = item.sheets.map((s) => `<tr class="sheet-row">
        <td colspan="2">${esc(s.drawingName || '')} — Rev ${esc(s.revisionLabel || '')} — Sheet ${esc(s.sheetNumber)}</td>
        <td class="qty-cell">${formatQty(s.quantity, item.unit)}</td>
      </tr>`).join('')
      return `<tr class="item-row">
        <td>${esc(itemCode)} ${esc(item.description)}</td>
        <td class="unit-cell">${esc(item.unit)}</td>
        <td class="qty-cell">${formatQty(item.total, item.unit)}</td>
      </tr>${sheetsHtml}`
    }).join('')
    return `<div class="category-block">
      <div class="category-header"><span class="cat-code">${codeLabel}</span> ${esc(cat.name.toUpperCase())}<span class="cat-total">${catTotalsHtml}</span></div>
      <table class="detail-table"><tbody>${itemsHtml}</tbody></table>
    </div>`
  }).join('')

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Construction Takeoff Report</title>
<style>
  @page { size: A4; margin: 18mm 14mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #0F172A; font-size: 10pt; margin: 0; }
  .report-header { border-bottom: 2px solid #0F172A; padding-bottom: 10px; margin-bottom: 16px; }
  .report-title { font-size: 9pt; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #64748B; }
  .report-subtitle { font-size: 16pt; font-weight: 700; margin-top: 2px; }
  .header-meta { display: flex; gap: 24px; margin-top: 10px; font-size: 9pt; color: #475569; }
  h2.section-title { font-size: 11pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; background: #0F172A; color: #fff; padding: 6px 10px; margin: 20px 0 0; }
  table.summary-table { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
  table.summary-table th { text-align: left; font-size: 8pt; text-transform: uppercase; color: #64748B; border-bottom: 2px solid #0F172A; padding: 6px 8px; }
  table.summary-table td { padding: 6px 8px; border-bottom: 1px solid #E2E8F0; font-size: 10pt; }
  .cat-cell { font-weight: 700; }
  .cat-code { display: inline-block; font-size: 8pt; font-weight: 700; color: #fff; background: #2563EB; border-radius: 3px; padding: 1px 5px; margin-right: 6px; }
  .unit-cell { text-align: center; color: #64748B; width: 70px; }
  .qty-cell { text-align: right; font-weight: 700; width: 120px; }
  .category-block { page-break-inside: avoid; margin-top: 14px; }
  .category-header { display: flex; align-items: center; gap: 8px; font-size: 11pt; font-weight: 700; border-left: 4px solid #2563EB; background: #F8FAFC; padding: 6px 10px; text-transform: uppercase; }
  .cat-total { margin-left: auto; font-size: 10pt; }
  table.detail-table { width: 100%; border-collapse: collapse; margin-left: 14px; }
  table.detail-table td { padding: 4px 8px; border-bottom: 1px solid #E2E8F0; font-size: 9.5pt; }
  .item-row td { font-weight: 600; }
  .sheet-row td { padding-left: 24px; color: #475569; font-size: 9pt; font-weight: 400; }
  .page-footer { position: fixed; bottom: 0; left: 0; right: 0; font-size: 8pt; color: #94A3B8; text-align: center; border-top: 1px solid #E2E8F0; padding-top: 4px; }
  @media print {
    .no-print { display: none; }
    tr { page-break-inside: avoid; }
  }
</style>
</head>
<body>
  <div class="report-header">
    <div class="report-title">Construction Takeoff Report</div>
    <div class="report-subtitle">Current Project Takeoff</div>
    <div class="header-meta">
      <div><strong>Tender:</strong> ${esc(model.header.tenderName)}</div>
      <div><strong>Drawings:</strong> ${model.header.drawingCount} (latest revision each)</div>
      <div><strong>Report Date:</strong> ${esc(model.header.reportDate)}</div>
      <div><strong>Prepared By:</strong> ${esc(model.header.preparedBy)}</div>
    </div>
  </div>

  <h2 class="section-title">Takeoff Summary</h2>
  <table class="summary-table">
    <thead><tr><th>Category</th><th>Unit</th><th style="text-align:right">Total Quantity</th></tr></thead>
    <tbody>${summaryRowsHtml}</tbody>
  </table>

  <h2 class="section-title">Detailed Breakdown</h2>
  ${categoriesHtml}

  <div class="page-footer">CostSpace Estimation — Construction Takeoff Report — Generated ${esc(model.header.reportDate)}</div>
</body>
</html>`
}
